import React, { useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebase';
import useEmailVerified from '../hooks/useEmailVerified';

// Account details, opened from the avatar menu at the top of every screen.
//
// Holds the two things a user needs to do with the address on their account:
// confirm it, and correct it. Both are here rather than on a Settings tab
// because the address is account-level, not per-tank — and because the most
// common reason to want it is a sign-up typo, where the only thing the user
// recognizes is their own name/initial in the corner.
//
// A modal, not a dropdown panel: the change form needs two fields and real
// explanatory copy, and the account popover is 260px wide.
export default function AccountModal({ open, onClose, user }) {
  const u = user || auth.currentUser || {};
  const email = u.email || '';

  const [emailVerified, recheckEmailVerified] = useEmailVerified(u);

  // Confirm-this-address link. status: idle | sending | sent | error
  const [verifyStatus, setVerifyStatus] = useState('idle');
  // Which address the mail came FROM, as reported by the server — so someone
  // staring at an empty inbox knows what to search their spam folder for.
  const [verifyFrom, setVerifyFrom] = useState('');

  // Change-the-address flow. step: 'idle' | 'form' | 'sending' | 'sent'
  const [step, setStep] = useState('idle');
  const [newEmail, setNewEmail] = useState('');
  const [newEmail2, setNewEmail2] = useState('');
  const [error, setError] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [sentFrom, setSentFrom] = useState('');

  const firstFieldRef = useRef(null);

  // Escape closes. Re-checking on open catches the common case: they
  // confirmed on their phone and came back to a tab with a stale token.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    recheckEmailVerified();
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Focus the first field when the form appears, not when the modal does —
  // opening straight onto a focused text input reads as an error prompt.
  useEffect(() => {
    if (step !== 'form') return undefined;
    const id = window.setTimeout(() => firstFieldRef.current?.focus(), 50);
    return () => window.clearTimeout(id);
  }, [step]);

  if (!open) return null;

  async function sendConfirmLink() {
    setVerifyStatus('sending');
    try {
      const res = await httpsCallable(functions, 'sendVerificationEmail')();
      if (res?.data?.alreadyVerified) {
        await recheckEmailVerified();
        setVerifyStatus('idle');
        return;
      }
      setVerifyFrom(res?.data?.sentFrom || '');
      setVerifyStatus('sent');
    } catch (err) {
      console.error('sendVerificationEmail failed:', err);
      setVerifyStatus('error');
    }
  }

  function openForm() {
    setNewEmail('');
    setNewEmail2('');
    setError('');
    setStep('form');
  }

  async function submitChange(e) {
    e.preventDefault();
    setError('');

    const a = newEmail.trim().toLowerCase();
    const b = newEmail2.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(a)) {
      setError('That doesn’t look like an email address.');
      return;
    }
    // Typing it twice is the point of the feature — the reason people are
    // here is a mistyped address, and a second entry catches a repeat of it
    // better than any format check can.
    if (a !== b) {
      setError('Those two addresses don’t match. Please re-enter them.');
      return;
    }
    if (a === email.toLowerCase()) {
      setError('That’s already the email on your account.');
      return;
    }

    setStep('sending');
    try {
      const res = await httpsCallable(functions, 'startEmailChange')({ newEmail: a });
      setSentTo(res?.data?.newEmail || a);
      setSentFrom(res?.data?.sentFrom || '');
      setStep('sent');
    } catch (err) {
      console.error('startEmailChange failed:', err);
      const code = err?.code || '';
      if (code.endsWith('already-exists')) {
        setError(err.message || 'Another SoKo account already uses that email address.');
      } else if (code.endsWith('resource-exhausted')) {
        setError('Too many requests just now. Please wait an hour and try again.');
      } else if (code.endsWith('invalid-argument')) {
        setError('That doesn’t look like an email address.');
      } else {
        setError('Something went wrong. Please try again in a moment.');
      }
      setStep('form');
    }
  }

  return (
    <div
      className="sms-modal-scrim"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="sms-modal account-modal" role="dialog" aria-modal="true" aria-labelledby="account-modal-title">
        <button type="button" className="sms-modal-close" aria-label="Close" onClick={onClose}>×</button>

        <h2 id="account-modal-title" className="sms-modal-title">Your account</h2>

        <div className="account-modal__label">Signed in as</div>
        <div className="account-modal__email">{email}</div>

        {emailVerified ? (
          <div className="settings-verify settings-verify--ok">✓ Email confirmed</div>
        ) : (
          <div className="settings-verify">
            {verifyStatus === 'sent' ? (
              <span className="hint" style={{ margin: 0 }}>
                Confirmation link sent to {email}
                {verifyFrom ? <> from <strong>{verifyFrom}</strong></> : null}.
                {' '}Open it to finish. If it isn’t there in a few minutes, check
                your spam or junk folder.
              </span>
            ) : (
              <>
                <span className="hint" style={{ margin: 0 }}>
                  {verifyStatus === 'error'
                    ? "Couldn't send the link just now. Please try again."
                    : 'This address isn’t confirmed yet.'}
                </span>
                <button
                  type="button"
                  className="btn btn-secondary settings-verify__btn"
                  onClick={sendConfirmLink}
                  disabled={verifyStatus === 'sending'}
                >
                  {verifyStatus === 'sending'
                    ? 'Sending…'
                    : (verifyStatus === 'error' ? 'Try again' : 'Send confirmation link')}
                </button>
              </>
            )}
          </div>
        )}

        <div className="divider" />

        {/* Changing the address IS changing the login, so the copy says so
            outright. Nothing moves until the link sent to the new address is
            opened, which is what makes this safe to offer at all. */}
        {step === 'sent' ? (
          <div className="account-modal__section">
            <div className="toast" style={{ margin: 0 }}>
              <span>✓</span>
              <span>
                Confirmation link sent to <strong>{sentTo}</strong>
                {sentFrom ? <> from <strong>{sentFrom}</strong></> : null}.
              </span>
            </div>
            <p className="hint" style={{ marginTop: 10, marginBottom: 0 }}>
              Open that email and click the link to finish. Nothing changes until
              you do — you’re still signing in with {email}. Can’t find it? Check
              your spam or junk folder.
            </p>
            <button type="button" className="linklike" style={{ marginTop: 10 }} onClick={openForm}>
              Use a different address
            </button>
          </div>
        ) : step === 'idle' ? (
          <div className="account-modal__section">
            <p className="hint" style={{ margin: '0 0 10px 0' }}>
              This is also the email you sign in with.
            </p>
            <button type="button" className="btn btn-secondary" onClick={openForm}>
              Change email address
            </button>
          </div>
        ) : (
          <form className="account-modal__section" onSubmit={submitChange}>
            <div className="account-modal__subhead">Change email address</div>
            <p className="hint" style={{ margin: '0 0 12px 0' }}>
              We’ll send a link to the new address. Once you open it, that becomes
              the email you sign in with — your password stays the same, and your
              tanks and alerts aren’t affected.
            </p>

            {error && (
              <div className="alert-banner fault" style={{ marginBottom: 12 }}>
                <div className="alert-banner-desc">{error}</div>
              </div>
            )}

            <div className="field">
              <label htmlFor="acctNewEmail">New email address</label>
              <input
                id="acctNewEmail"
                ref={firstFieldRef}
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                disabled={step === 'sending'}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="acctNewEmail2">Type it again</label>
              <input
                id="acctNewEmail2"
                type="email"
                value={newEmail2}
                onChange={(e) => setNewEmail2(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                disabled={step === 'sending'}
                required
              />
            </div>

            <div className="account-modal__actions">
              <button type="submit" className="btn btn-primary" disabled={step === 'sending'}>
                {step === 'sending' ? 'Sending…' : 'Send confirmation link'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => { setStep('idle'); setError(''); }}
                disabled={step === 'sending'}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
